import { PRODUCTS_INDEX, SERVICES_INDEX } from '@constant/common';
import { disableLoader, showSuccess } from '@context/actions';
import { replaceAppointmentServices } from '@context/actions/appointment';
import { replaceOrderIitems } from '@context/actions/order';
import { useSharedLinkInitialData } from '@hook/sharedLinkData';
import { useSharedLinkId, useSharedLinkResource } from '@hook/useSharedLinkResource';
import SharedLinkState from '@module/sharedLinkState';
import { getOrderByOrderId } from '@storeData/order';
import { getUserByGuestId } from '@storeData/user';
import { navigateTo } from '@util/routerService';
import { readLocalStorage, removeLocalStorage } from '@util/safeStorage';
import { formatTimeTo12Hr, formatToDateFormat, isActiveAtBinaryIndex, processAppliedTaxes } from '@util/utils';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useState } from 'react';
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from 'react-redux';
import { useConversionTracking } from '../../../hooks/useConversionTracking';
import { CAT_ORDER_CONFIRMATION } from '@constant/userSearchSource';

function OrderConfirmation({ inititalOrder = null }: any) {
    const [cookie] = useCookies();
    const [userData, setUserCookie] = useState(cookie['user']);
    const activeGroup = useSelector((state: any) => state.activeGroup);
    const dispatch = useDispatch();
    const router = useRouter()
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [posOrder, setPosOrder] = useState<any>(false)
    const { configData } = useSelector((state: any) => state.store ? state.store.storeData : null)
    const [pricingBreakdown, setPricingBreakdown] = useState({ total: 0, subTotal: 0, appliedTaxes: [] });
    const isDisposableAvailable = configData?.storeConfig?.basicConfig?.disposable_items;
    const basicConfig = configData?.storeConfig?.basicConfig
    const store = useSelector((state: any) => state);
    const languageData: any = store.languageKeywords
    const currentStoreId = store?.store?.storeData?.storeId || configData?.storeId;
    const activeLanguage: any = store.activeLanguage
    const appointmentItems = useSelector((state: any) => state.appointmentServices);
    const cartItems = useSelector((state: any) => state.orderItems);
    const sharedOrderId = useSharedLinkId();
    const [confirmationError, setConfirmationError] = useState(false);
    const isStandalonePaidLink = !inititalOrder
        && router.isReady
        && router.query.status === 'paid'
        && Boolean(sharedOrderId);
    // Some gateways redirect back with `status=paid` but without the order id. The payment did
    // succeed, so the failed-payment copy below must not be shown.
    const isPaidLinkWithoutOrder = !inititalOrder
        && router.isReady
        && router.query.status === 'paid'
        && !sharedOrderId;
    const {
        data: fetchedOrder,
        state: orderFetchState,
        retry: retryOrder
    } = useSharedLinkResource<any>({
        id: sharedOrderId,
        initialData: useSharedLinkInitialData('order', sharedOrderId),
        fetcher: (id) => getOrderByOrderId(id, currentStoreId, configData?.tenantId),
        enabled: isStandalonePaidLink
    });
    const updateLocalOrder = (orderData: any) => {
        const products = orderData.products || [];
        const uniqueTypes = [...new Set(products.map((item: any) => item.type))];
        orderData.products = products.map((item: any) => {
            let originalItem = appointmentItems.find((i) => i.name == item.name) || cartItems.find((i) => i.name == item.name);
            if (originalItem) {
                item.languages = originalItem.languages;
            }

            return item;
        });
        setOrderData({ ...orderData, uniqueTypes });
        getPricingBreakdown(orderData)
    }
    const appointmentConfig = configData?.storeConfig?.appointmentConfig;

    useEffect(() => {
        dispatch(replaceAppointmentServices([]));
        dispatch(replaceOrderIitems([]));

        if (Boolean(inititalOrder)) {
            setOrderSuccess(true);
            inititalOrder.taxesTotal = inititalOrder?.total - inititalOrder?.subtotal;
            updateLocalOrder(inititalOrder)
        } else {
            dispatch(disableLoader());
            const onConfirmationRoute = Array.isArray(router.query.pagepath) && router.query.pagepath.includes("orderconfirmation");
            // A shared paid link is handled even if the path check above misses it, otherwise the
            // fetched order never reaches local state and the page waits on a loader forever.
            if (onConfirmationRoute || isStandalonePaidLink) {
                if (router.query.status == 'paid') {
                    if (router.query.posOrder == 'true') {
                        setPosOrder(true);
                    }
                    if (fetchedOrder) {
                        try {
                            setConfirmationError(false);
                            setOrderSuccess(true);
                            updateLocalOrder(fetchedOrder)
                        } catch (error) {
                            console.error("Order confirmation error:", error);
                            setOrderSuccess(false);
                            setConfirmationError(true);
                        }
                        getUserByGuestId(fetchedOrder?.tenantId, fetchedOrder?.storeId, fetchedOrder.guestId, CAT_ORDER_CONFIRMATION).then((response) => {
                            setUserCookie(response)
                        }).catch(function (error) {
                            console.error("Guest data error:", error);
                        });
                    }
                } else {
                    setOrderSuccess(false);
                    dispatch(showSuccess(`${languageData['Your payment has been failed'] ? languageData['Your payment has been failed'] : 'Your payment has been failed.'}`, 5000));
                    let paymentFor = readLocalStorage("payment_type")
                    if (paymentFor) {
                        if (paymentFor.includes('order')) navigateTo('cart')
                        if (paymentFor.includes('appointment')) navigateTo('appointment')
                    } else {
                        navigateTo('home')
                    }
                    removeLocalStorage("payment_type");
                }
            }
        }
    }, [router.isReady, router.query.status, router.query.posOrder, fetchedOrder, inititalOrder, sharedOrderId])

    const calculateTaxes = (appliedTaxes, taxebalePrice: any, type: any, taxCharges) => {
        const hasItemTax = Boolean(taxCharges?.length);
        const taxesToProcess = hasItemTax ? taxCharges : (configData.txchConfig || []);
        const filteredTax = taxesToProcess.filter(taxData =>
            taxData.active && (hasItemTax || (((isActiveAtBinaryIndex(taxData.applyOn, PRODUCTS_INDEX) && type == "product") || (isActiveAtBinaryIndex(taxData.applyOn, SERVICES_INDEX) && type == "service")) && !taxData.charge)));

        return processAppliedTaxes(filteredTax, taxebalePrice, appliedTaxes);
    }
    const getPricingBreakdown = (order) => {
        let total: any = 0;
        let subTotal: any = 0;
        let appliedTaxes: any[] = [];
        order.products.map((cartItem) => {
            let applicablePrice: any = (userData?.membership?.length == 1 && userData?.membership[0].typeId == 4) ? (cartItem.memberPrice) * cartItem.quantity : (cartItem.salePrice || cartItem.price) * cartItem.quantity;
            subTotal += parseFloat(applicablePrice);
            total += parseFloat(applicablePrice);
            appliedTaxes = calculateTaxes(appliedTaxes, applicablePrice, cartItem.type, cartItem.taxCharges)
        })
        appliedTaxes.map((taxData: any) => {
            if (!taxData.isInclusive) total = Number((total + taxData.total).toFixed(2));
        })
        const dispTotal = Boolean(order?.disposables?.length) ? order?.disposables?.reduce((a: any, b: any) => a + (Number(b.price) * Number(b.quantity)), 0) : 0;
        total = dispTotal + Number(total);
        setPricingBreakdown({ total, subTotal, appliedTaxes });
    }

    const AMPConversionTags = () => {
        const { fireConversion } = useConversionTracking(Number(configData?.tenantId));

        // Fire purchase conversion when order is confirmed
        useEffect(() => {
            if (orderData && pricingBreakdown) {
                fireConversion('purchase', {
                    value: pricingBreakdown.total,
                    currency: configData?.currencySymbol,
                    orderId: orderData.orderId,
                    products: orderData.products?.map((product: any) => ({
                        id: product.id,
                        name: product.name,
                        price: product.price,
                        quantity: product.quantity
                    }))
                });
            }
        }, [orderData, pricingBreakdown, fireConversion]);

        return null;
    }

    const getRescheduleConcellationInfo = () => {

        if (Boolean(orderData?.appointmentId) && Boolean(!posOrder)) {
            let rescheduleInfo = "";
            let cancellationInfo = "";
            if (Boolean(appointmentConfig.enableCatalogReschedule) && Boolean(appointmentConfig.enableCatalogCancellation) && Number(appointmentConfig?.rescheduleLimitHrs) == Number(appointmentConfig?.cancellationLimitHrs)) {
                rescheduleInfo = `Appointment can be cancelled or rescheduled up to ${appointmentConfig?.rescheduleLimitHrs} hours before the scheduled time`;
                return <div className="username instruction">{rescheduleInfo}</div>
            }
            if (Boolean(appointmentConfig.enableCatalogReschedule) && Boolean(appointmentConfig?.rescheduleLimitHrs)) {
                rescheduleInfo = `Appointment can be rescheduled up to ${appointmentConfig?.rescheduleLimitHrs} hours before the scheduled time`;
            }
            if (Boolean(appointmentConfig.enableCatalogCancellation) && Boolean(appointmentConfig?.cancellationLimitHrs)) {
                cancellationInfo = `Appointment can be cancelled up to ${appointmentConfig?.cancellationLimitHrs} hours before the scheduled time`;
            }
            return <>
                {rescheduleInfo && <div className="username instruction">{rescheduleInfo}</div>}
                {cancellationInfo && <div className="username instruction">{cancellationInfo}</div>}
            </>
        } else return null
    }

    // Without the router we cannot yet tell a paid link from a failed payment, and the fallback
    // below renders the failure copy. Hold on a loader until the query params resolve.
    const awaitingRouter = !inititalOrder && !router.isReady;
    const awaitingOrder = isStandalonePaidLink && (orderFetchState !== 'ready' || !orderData);

    const resolveConfirmationState = () => {
        if (awaitingRouter) return 'loading';
        // The order arrived but could not be turned into a confirmation, so offer a retry
        // instead of leaving the customer on a loader that never resolves.
        if (confirmationError) return 'error';
        if (isPaidLinkWithoutOrder) return 'notFound';
        return orderFetchState === 'ready' ? 'loading' : orderFetchState;
    };

    if (awaitingRouter || isPaidLinkWithoutOrder || awaitingOrder) {
        return <SharedLinkState
            state={resolveConfirmationState()}
            onRetry={isPaidLinkWithoutOrder ? undefined : retryOrder}
            onSecondaryAction={() => navigateTo('home')}
            loadingText="Loading your order confirmation..."
            notFoundText="The confirmed order is not available."
            errorText="We couldn't load your confirmed order. Please check your connection and try again."
            secondaryActionText="Explore more services"
        />;
    }

    return (
        <>
            {orderSuccess ? <div className={`con-main-wrap clearfix`}>
                <div className="main-wrapper order-confirmation" style={{ background: 'transparent' }}>
                    <div className='appointment-checkout-content'>
                        <AMPConversionTags />
                        <div className="con-wrap">
                            {/* <div className='image-wrap'>
                                <img className="cart-logo" alt="Respark" src={`/assets/images/${activeGroup}/order_confirm.png`} />
                            </div> */}
                            {Boolean(orderData?.appointmentId) && Boolean(!posOrder) ?
                                <div className="username instruction appt-status-note cap-text">
                                    {Boolean(appointmentConfig.autoConfirmed) ?
                                        <div> {languageData["We have received your appointment and it is confirmed"] ? languageData["We have received your appointment and it is confirmed"] : "We have received your appointment and it is confirmed"}.</div> :
                                        <div>Hi {userData?.firstName}, {languageData["your appointment has been initiated for the below services. Kindly wait for our confirmation."] ? languageData["your appointment has been initiated for the below services. Kindly wait for our confirmation."] : "your appointment has been initiated for the below services. Kindly wait for our confirmation."}.</div>
                                    }</div>
                                : <><div className="cart-status">{userData?.firstName}</div>
                                    <div className="cart-subtext">
                                        {languageData["Thank you for your purchase"] ? languageData["Thank you for your purchase"] : "Thank you for your purchase"}.
                                    </div>
                                    {!posOrder && (
                                        orderData?.type === "delivery" ? (
                                            <div className="username instruction">{configData?.deliveryDisclaimer}</div>) : (
                                            <div className="username instruction">{configData?.pickupDisclaimer}</div>
                                        )
                                    )}
                                </>
                            }
                        </div>

                        <div className="date-id-wrap card">
                            <div className="date-time">
                                <div className='sub-heading'>{languageData["Date"] ? languageData["Date"] : "Date"}:</div>
                                <div className="date">{formatToDateFormat(orderData?.orderDay, configData.dateFormat)}{Boolean(orderData?.orderTime) && <>, {formatTimeTo12Hr(orderData?.orderTime)}</>}
                                </div>
                            </div>
                            {Boolean(orderData?.orderId) && <div className="date-time">
                                <div className='sub-heading'>{languageData["Order Id"] ? languageData["Order Id"] : "Order Id"}:</div>
                                <div className="date">{orderData?.orderId}</div>
                            </div>}
                        </div>

                        {Boolean(orderData?.bookedFor) && <div className="date-id-wrap card">
                            <div className="date-time">
                                <div className='sub-heading'>{languageData["Booked For"] ? languageData["Booked For"] : "Booked For"}:</div>
                                <div className="date cap-text">{orderData?.bookedFor?.name} {basicConfig.multipleCountryCode ? ("+" + orderData?.bookedFor?.countryCode + " " + orderData?.bookedFor?.mobileNo) : (orderData?.bookedFor?.mobileNo)}</div>
                            </div>
                        </div>}

                        {Boolean(orderData?.selectedExpert) && <div className="date-id-wrap card">
                            <div className="date-time">
                                <div className='sub-heading'>{languageData["Preferred Stylist"] ? languageData["Preferred Stylist"] : "Preferred Stylist"}:</div>
                                <div className="date cap-text">{orderData?.selectedExpert?.firstName} {orderData?.selectedExpert?.lastName}</div>
                            </div>
                        </div>}

                        {(Boolean(inititalOrder || orderData) && Boolean((inititalOrder || orderData).products[0].staff)) ? <div className="services-list-wrap card">
                            <div className="sub-heading">{languageData["Services"] ? languageData["Services"] : "Services"}</div>
                            <div className='service-details heading'>
                                <div className="service-name" >{languageData["Name"] ? languageData["Name"] : "Name"}</div>
                                {Boolean(!posOrder) && <div className="service-name slot" >{languageData["Slot"] ? languageData["Slot"] : "Slot"}</div>}
                                <div className="service-name expert" >{languageData["Expert"] ? languageData["Expert"] : "Expert"}</div>
                                <div className="service-name expert">{languageData["Price"] ? languageData["Price"] : "Price"}</div>
                            </div>
                            {(inititalOrder || orderData).products?.map((item, index) => {
                                return <div className='service-details' key={index}>
                                    <div className="service-name" >{item.languages && item.languages[activeLanguage?.languageId]?.name ? item.languages[activeLanguage?.languageId]?.name : item.name}</div>
                                    {Boolean(!posOrder) && <div className="service-name slot" >{formatTimeTo12Hr(item.slot)}</div>}
                                    <div className="service-name expert" >{item?.staff || ''}</div>
                                    <div className="service-name expert">{configData?.currencySymbol}{(item.salePrice && item.salePrice < item.billingPrice) ? item.salePrice : item.billingPrice}</div>
                                </div>
                            })}
                        </div> : <>
                            {orderData?.uniqueTypes?.map((type: any) => {
                                return <Fragment key={type}>
                                    {Boolean(orderData?.products?.length) && <div className="services-list-wrap card">
                                        <div className="sub-heading cap-text">{type + "s"}</div>
                                        {orderData?.products?.map((item) => {
                                            return <div className="service-name" key={Math.random()}>
                                                <div>{item?.languages && item?.languages[activeLanguage?.languageId]?.name ? item.languages[activeLanguage?.languageId]?.name : item.name}</div>
                                                <div>{configData?.currencySymbol}{item.billingPrice}</div>
                                            </div>
                                        })}
                                    </div>}
                                </Fragment>
                            })}
                        </>}


                        {Boolean(orderData?.disposables?.length) && <div className="services-list-wrap card">
                            <div className="sub-heading cap-text">{languageData["Extra Charges"] ? languageData["Extra Charges"] : "Extra Charges"}</div>
                            {orderData?.disposables?.map((item) => {
                                return <div className="service-name" key={item.name}>
                                    <div>{item.languages && item.languages[activeLanguage?.languageId]?.name ? item.languages[activeLanguage?.languageId]?.name : item.name} {item.quantity > 1 && <> &nbsp;({item.quantity})</>}</div>
                                    <div>{configData?.currencySymbol}{(item.price) * (item.quantity)}</div>
                                </div>
                            })}
                        </div>}

                        <div className="appt-for-wrap card">
                            <div className='sub-heading'>{languageData["Payment Summary"] ? languageData["Payment Summary"] : "Payment Summary"}</div>
                            <div className='pricing-details-wrap d-f-c'>
                                <div className='heading'>
                                    <div className='title'>{languageData["SubTotal"] ? languageData["SubTotal"] : "SubTotal"}</div>
                                    {(isDisposableAvailable && Boolean(orderData?.disposables?.length)) && <div className='title'>Extra Charges</div>}
                                    {pricingBreakdown.appliedTaxes?.map((taxData: any) => {
                                        return <div className='title' key={taxData.name}>{taxData.name}{taxData.isInclusive ? "(Inclusive)" : ''}({taxData.value}%)</div>
                                    })}
                                    <div className='title grand-total'>{languageData["Grand Total"] ? languageData["Grand Total"] : "Grand Total"}</div>
                                    {posOrder && Boolean(orderData?.totalTipAmount) && <div className='title grand-total'>{languageData["Tip Amount"] ? languageData["Tip Amount"] : "Tip Amount"}</div>}
                                </div>
                                <div className='details'>
                                    <div className='value'>{configData.currencySymbol} {pricingBreakdown.subTotal}</div>
                                    {(isDisposableAvailable && Boolean(orderData?.disposables?.length)) && <div className='value'>{configData.currencySymbol} {orderData?.disposables?.reduce((a: any, b: any) => a + (Number(b.price) * Number(b.quantity)), 0)}</div>}
                                    {pricingBreakdown.appliedTaxes?.map((taxData: any, index: number) => {
                                        return <div className='value' key={taxData.name}>{configData.currencySymbol} {taxData.total}</div>
                                    })}
                                    <div className='value grand-total'>{configData.currencySymbol} {pricingBreakdown.total}</div>
                                    {posOrder && Boolean(orderData?.totalTipAmount) && <div className='value grand-total'>{configData.currencySymbol} {orderData?.totalTipAmount}</div>}
                                </div>
                            </div>
                        </div>

                        {Boolean(orderData?.payments?.length) && <div className="date-id-wrap card">
                            <div className="sub-heading">{languageData["Amount Paid"] ? languageData["Amount Paid"] : "Amount Paid"}</div>
                            {orderData?.payments?.map((paymode: any, i: number) => {
                                return <div key={paymode?.name} className='sub-heading '>{paymode?.name}: {configData.currencySymbol}{paymode.payment}</div>
                            })}
                        </div>}
                        {Boolean(orderData?.appointmentId) && Boolean(!posOrder) && getRescheduleConcellationInfo()}

                        {Boolean(inititalOrder) && <div className='confirm-btn-wrap book-wrap alignment'>
                            <button className="primary-btn rounded-btn" onClick={() => navigateTo('home')}>{languageData["Explore More"] ? languageData["Explore More"] : "Explore More"}</button>
                        </div>}
                    </div>
                </div>
            </div>
                :
                <div className="con-main-wrap">
                    <div className="con-wrap">
                        <div><img className="cart-logo" alt="Respark" src={`/assets/images/${activeGroup}/order_confirm.png`} style={{ width: '70%' }} /></div>
                        <div className="cart-status">{userData?.firstName}</div>
                        <div className="cart-subtext">{languageData["Ohh! Your payment has been failed"] ? languageData["Ohh! Your payment has been failed"] : "Ohh! Your payment has been failed"}.</div>
                        {/* <div className="cart-subtext">Your item is ready for pickup, kindly collect it from frontdesk.</div> */}
                        <div><button className="cart-button" onClick={() => navigateTo('home')}>{languageData["Explore More"] ? languageData["Explore More"] : "Explore More"}</button></div>
                    </div>
                </div>}
        </>
    )
}

export default OrderConfirmation;
